import { Component } from 'react';
import type { MouseEvent } from 'react';
import { Link } from 'react-router';
import { Button, Modal } from 'react-bootstrap';

import Icon, { icons } from '../../generic/Icon';
import Loader from '../../generic/Loader';
import Locator from '../../../../shared/Locator';
import { nuc } from '../../../utils';
import AccessGate from '../../generic/Gates/AccessGate';
import { put, remove } from '../../../redux/api';
import { renderPackage, renderRevision } from '../../../../shared/helpers/renderLocator';

class ModalDeleteDependency extends Component<
  {
    parent_locator: string;
    onDeleteDependency?: Function;
    onIgnoreDependency?: Function;
    revision?: {
      locator: string;
      project: { title: string };
      manual?: boolean;
    };
    show?: boolean;
    onHide: (e?: MouseEvent<{}>) => void;
    onProjectChange: () => void;
  },
  {
    loading?: boolean;
  }
> {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
    };
  }

  onDeleteDependency = async () => {
    const { revision, parent_locator, onDeleteDependency, onHide, onProjectChange } = this.props;
    // Cant delete a revision that doesnt exist
    if (!revision?.locator) {
      return;
    }

    this.setState({ loading: true });
    await remove<void, void>(
      `/api/revisions/${nuc(parent_locator)}/dependencies/${nuc(revision?.locator)}`,
    );

    if (onDeleteDependency) {
      await onDeleteDependency(revision);
    }

    onProjectChange();

    this.setState({ loading: false });
    onHide();
  };

  onIgnoreDependency = async () => {
    const { parent_locator, revision, onIgnoreDependency, onHide, onProjectChange } = this.props;
    // Cant ignore a revision that doesnt exist
    if (!revision?.locator) {
      return;
    }

    this.setState({ loading: true });
    await put(`/api/revisions/${nuc(parent_locator)}/dependencies/${nuc(revision.locator)}/ignore`, undefined);
    if (onIgnoreDependency) {
      await onIgnoreDependency(this.props.revision);
    }
    onProjectChange();

    this.setState({ loading: false });
    onHide();
  };

  renderModalBody = () => {
    const parent_locator = new Locator(this.props.parent_locator);
    const parent_project = parent_locator.toPackageString();

    return (
      <div>
        <p>
          <Icon icon={icons.infoCircle} />&nbsp;
          {this.props.revision && this.props.revision.manual ? (
            <b>This dependency was added manually to this revision. It will be removed from this revision, and all future revisions.</b>
          ) : (
            <b>
              This dependency will be ignored for all past, present, and future revisions. This action
              can be undone in{' '}
              <Link
                style={{ textDecoration: 'underline', color: '#337ab7' }}
                activeClassName="active"
                to={{
                  pathname: `/projects/${nuc(parent_project)}/settings`,
                }}>
                project settings
              </Link>
            </b>
          )}
        </p>
      </div>
    );
  };

  render() {
    const proj_locator = this.props.revision ? new Locator(this.props.revision.locator) : null;

    return (
      <AccessGate behavior="showModal" level="free" show={this.props.show} onHide={this.props.onHide}>
        <Modal show={this.props.show} onHide={this.props.onHide}>
          <Modal.Header closeButton>
            {this.props.revision ? (
              <Modal.Title>
                {this.props.revision.manual ? 'Delete' : 'Ignore'}&nbsp;
                {this.props.revision.project ? this.props.revision.project.title : renderPackage(proj_locator)} ({
                  renderRevision(proj_locator)
                })
              </Modal.Title>
            ) : (
              <Modal.Title>Loading...</Modal.Title>
            )}
          </Modal.Header>
          <Modal.Body>{this.state.loading || !this.props.revision ? <Loader /> : this.renderModalBody()}</Modal.Body>
          <Modal.Footer>
            <Button onClick={this.props.onHide}>Cancel</Button>
            <Button
              bsStyle="danger"
              onClick={() => {
                if (this.props.revision.manual) {
                  this.onDeleteDependency();
                } else {
                  this.onIgnoreDependency();
                }
              }}>
              Confirm
            </Button>
          </Modal.Footer>
        </Modal>
      </AccessGate>
    );
  }
}

export default ModalDeleteDependency;
